import { Link } from 'react-router'

export function PrivacyPage() {
  return (
    <div className="bg-background text-foreground min-h-screen">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-8">
          <Link to="/" className="text-primary hover:text-primary/80 text-sm font-medium">
            &larr; Back to CASSI
          </Link>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Privacy Policy</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            MAGIG Design + Technologies (magig.de)
          </p>
          <p className="text-muted-foreground text-sm">Last updated: January 23, 2026</p>
        </header>

        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <p className="text-muted-foreground lead">
            The following information applies to the processing of personal data on the websites and
            in the app of MAGIG Design + Technologies ("we," "us," or "our"). This includes, in
            particular, the websites magig.de and getcassi.ai (a product of MAGIG). This Privacy
            Policy is intended to inform you about the nature and scope of data processing, its
            purposes, recipients, legal bases, storage periods, and your rights as a user.
          </p>
          <p className="text-muted-foreground">
            "Personal data" refers to all information relating to an identifiable natural person
            (data subject), such as name, address, or email address.
          </p>

          <hr className="border-border my-8" />

          <Section title="1. Name and Address of the Controller">
            <p>
              The controller within the meaning of the General Data Protection Regulation (GDPR) and
              other national data protection laws is:
            </p>
            <address className="not-italic">
              Dr. Ingo Kiedaisch
              <br />
              MAGIG Design + Technologies UG
              <br />
              Hochallee 19
              <br />
              20149 Hamburg
              <br />
              Germany
            </address>
          </Section>

          <Section title="2. Contact Details of the Data Protection Officer">
            <p>
              You can reach the Data Protection Officer responsible for these websites and apps at:
            </p>
            <address className="not-italic">
              MAGIG Design + Technologies UG
              <br />
              Data Protection Officer
              <br />
              Hochallee 19
              <br />
              20149 Hamburg
              <br />
              Germany
            </address>
          </Section>

          <Section title="3. General Information About Data Processing">
            <h4 className="text-foreground font-semibold">
              (a) Legal Bases for Processing Personal Data:
            </h4>
            <ul>
              <li>Article 6(1)(a) GDPR (consent)</li>
              <li>Article 6(1)(b) GDPR (performance of a contract or pre-contractual measures)</li>
              <li>Article 6(1)(c) GDPR (legal obligation)</li>
              <li>
                Article 6(1)(f) GDPR (legitimate interests), provided these are not overridden by
                the data subject's higher-ranking interests or rights.
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">(b) Data Deletion and Storage Period:</h4>
            <p>
              We delete or block personal data as soon as the purpose of storage no longer applies.
              Further storage takes place only if required by law. After the expiration of such a
              statutory period, we delete or block the data unless further retention is necessary
              for concluding or performing a contract.
            </p>
          </Section>

          <Section title="4. Providing the Websites (magig.de, getcassi.ai) and Creating Log Files">
            <h4 className="text-foreground font-semibold">
              (a) Description and Scope of Data Processing:
            </h4>
            <p>
              Each time our websites (e.g., magig.de, getcassi.ai) or apps are accessed, our system
              automatically collects data from the user's device. The following data, among others,
              may be collected:
            </p>
            <ul>
              <li>Browser type, version, installed plugins, and language</li>
              <li>Operating system and version of the accessing device</li>
              <li>IP address of the accessing device</li>
              <li>Date and time of access, duration of visit, and pages viewed</li>
              <li>Page load times (average per session)</li>
              <li>Websites from which the accessing device reached our websites</li>
              <li>Websites the accessing device calls up via our websites</li>
            </ul>
            <p>
              These data are stored in log files. They are not stored together with other personal
              data.
            </p>

            <h4 className="text-foreground font-semibold">(b) Legal Basis:</h4>
            <p>Article 6(1)(f) GDPR (legitimate interests).</p>

            <h4 className="text-foreground font-semibold">(c) Purpose of Data Processing:</h4>
            <p>
              Temporarily storing the IP address is necessary to deliver the relevant website to the
              user's device. Storing log files serves the functionality and security of our systems.
              We do not use these data for marketing purposes.
            </p>

            <h4 className="text-foreground font-semibold">(d) Storage Period:</h4>
            <p>
              Data are deleted as soon as they are no longer required for the purpose of their
              collection. Generally, this occurs no later than 7 days, unless further storage (in
              anonymized form) is technically required.
            </p>

            <h4 className="text-foreground font-semibold">(e) Objection and Removal Option:</h4>
            <p>
              Collecting data for providing our websites and storing them in log files is absolutely
              necessary for operation; therefore, there is no option to object.
            </p>
          </Section>

          <Section title="5. Use of Cookies">
            <h4 className="text-foreground font-semibold">
              (a) Description and Scope of Data Processing:
            </h4>
            <p>
              Our websites (magig.de, getcassi.ai) use technically necessary cookies (text files)
              that are stored on the user's device.
            </p>

            <h4 className="text-foreground font-semibold">(b) Legal Basis:</h4>
            <p>Article 6(1)(f) GDPR (legitimate interests).</p>

            <h4 className="text-foreground font-semibold">(c) Purpose of Data Processing:</h4>
            <p>
              Technically necessary cookies make the visit more user-friendly and enable certain
              features that would otherwise be only partially available on our websites.
            </p>

            <h4 className="text-foreground font-semibold">
              (d) Storage Period, Objection, and Removal Option:
            </h4>
            <p>
              Cookies are stored on the user's device and transmitted from it to our sites. Users
              can disable or restrict the use of cookies at any time in their browser settings. This
              may lead to functional limitations.
            </p>
          </Section>

          <Section title="6. Contact by Email or Phone">
            <h4 className="text-foreground font-semibold">
              (a) Description and Scope of Data Processing:
            </h4>
            <p>
              If you contact us by email or phone, we process the information you provide (e.g.,
              email address, name, phone number) solely to handle your inquiry.
            </p>

            <h4 className="text-foreground font-semibold">(b) Legal Basis:</h4>
            <p>
              Article 6(1)(f) GDPR (legitimate interests). If the contact aims at entering into a
              contract, Article 6(1)(b) GDPR (contract performance) also applies.
            </p>

            <h4 className="text-foreground font-semibold">(c) Purpose of Data Processing:</h4>
            <p>
              We use these data for the purpose of communicating with you and handling your request.
            </p>

            <h4 className="text-foreground font-semibold">(d) Storage Period:</h4>
            <p>
              We delete your data as soon as it is no longer needed for your inquiry, provided no
              statutory retention obligations apply.
            </p>
          </Section>

          <Section title="7. Social Media Platform Integrations">
            <p>
              Our application integrates with the following social media platforms to enable you to
              create, manage, and publish content:
            </p>
            <ul>
              <li>
                <strong>Instagram</strong> (Meta Platforms)
              </li>
              <li>
                <strong>LinkedIn</strong> (Microsoft)
              </li>
              <li>
                <strong>TikTok</strong> (ByteDance)
              </li>
              <li>
                <strong>YouTube</strong> (Google)
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">
              (a) Data We Collect from Social Media Platforms
            </h4>
            <p>
              When you connect a social media account, we collect the following data via OAuth
              authentication:
            </p>

            <p>
              <strong>Instagram:</strong>
            </p>
            <ul>
              <li>User ID and username</li>
              <li>Display name and biography</li>
              <li>Profile picture URL</li>
              <li>Follower, following, and media counts</li>
            </ul>

            <p>
              <strong>LinkedIn:</strong>
            </p>
            <ul>
              <li>User ID</li>
              <li>First and last name</li>
              <li>Professional headline</li>
              <li>Profile picture</li>
              <li>Connected organization details (if applicable)</li>
            </ul>

            <p>
              <strong>TikTok:</strong>
            </p>
            <ul>
              <li>Open ID (TikTok user identifier)</li>
              <li>Display name and bio</li>
              <li>Avatar URL</li>
              <li>Profile deep link</li>
            </ul>

            <p>
              <strong>YouTube/Google:</strong>
            </p>
            <ul>
              <li>Channel ID and title</li>
              <li>Channel description and thumbnail</li>
              <li>Subscriber count, video count, and view count</li>
            </ul>

            <h4 className="text-foreground font-semibold">(b) Authentication Tokens</h4>
            <p>For each connected platform, we store:</p>
            <ul>
              <li>Access tokens (to make API requests on your behalf)</li>
              <li>Refresh tokens (to maintain your connection without repeated logins)</li>
              <li>Token expiration timestamps</li>
            </ul>

            <h4 className="text-foreground font-semibold">(c) Legal Basis:</h4>
            <p>
              Article 6(1)(b) GDPR (performance of a contract) — connecting your social media
              accounts is necessary to provide our content creation and publishing services.
            </p>

            <h4 className="text-foreground font-semibold">(d) Purpose of Data Processing:</h4>
            <ul>
              <li>
                <strong>Authentication:</strong> To verify your identity and authorize access to
                your social media accounts
              </li>
              <li>
                <strong>Profile Display:</strong> To show your connected accounts within our
                application
              </li>
              <li>
                <strong>Content Publishing:</strong> To post content you create to your connected
                platforms
              </li>
              <li>
                <strong>Post Import:</strong> To import your existing posts for analysis and content
                inspiration (only when you initiate this action)
              </li>
            </ul>
          </Section>

          <Section title="8. Google Services Integration">
            <h4 className="text-foreground font-semibold">(a) Google Drive Integration</h4>
            <p>
              We offer optional integration with Google Drive to allow you to upload files from your
              Drive to our application.
            </p>
            <p>
              <strong>Data collected:</strong>
            </p>
            <ul>
              <li>Google user ID and email address</li>
              <li>OAuth access and refresh tokens</li>
              <li>Granted permission scopes</li>
            </ul>
            <p>
              <strong>Purpose:</strong> To enable file uploads from your Google Drive to our
              application for use in content creation.
            </p>

            <h4 className="text-foreground font-semibold">(b) YouTube Integration</h4>
            <p>As described in Section 7, we integrate with YouTube to enable video publishing.</p>
            <p>
              <strong>How we use YouTube/Google user data:</strong>
            </p>
            <ul>
              <li>
                We access your YouTube channel information solely to display your connected account
                and enable video publishing
              </li>
              <li>We do not access, store, or process your YouTube viewing history</li>
              <li>We do not use your Google data for advertising or marketing purposes</li>
              <li>
                We do not share your Google data with third parties except as necessary to provide
                our services (see Section 11)
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">(c) Legal Basis:</h4>
            <p>
              Article 6(1)(a) GDPR (consent) — you explicitly authorize access when connecting your
              Google account.
            </p>

            <h4 className="text-foreground font-semibold">
              (d) Google API Services Limited Use Disclosure
            </h4>
            <p>
              Our use of information received from Google APIs adheres to the{' '}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements. Specifically:
            </p>
            <ul>
              <li>
                We only use Google data to provide and improve user-facing features that are visible
                to you
              </li>
              <li>
                We do not transfer Google data to third parties except as necessary to provide our
                services, comply with applicable laws, or with your explicit consent
              </li>
              <li>We do not use Google data for serving advertisements</li>
              <li>
                We do not allow humans to read Google data unless we have your affirmative agreement
                for specific messages, it is necessary for security purposes (such as investigating
                abuse), to comply with applicable law, or for our internal operations where the data
                has been aggregated and anonymized
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">(e) LinkedIn Data Usage</h4>
            <p>
              Our use of LinkedIn data complies with the{' '}
              <a
                href="https://www.linkedin.com/legal/l/api-terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline">
                LinkedIn API Terms of Use
              </a>
              . Specifically:
            </p>
            <ul>
              <li>We only access LinkedIn data that you explicitly authorize</li>
              <li>
                We do not use LinkedIn data for recruiting, advertising, or marketing purposes
                without your consent
              </li>
              <li>
                We do not scrape, crawl, or use automated means to access LinkedIn data outside of
                the official API
              </li>
              <li>We do not store LinkedIn data longer than necessary to provide our services</li>
              <li>We do not sell or license LinkedIn data to third parties</li>
            </ul>

            <h4 className="text-foreground font-semibold">(f) TikTok Data Usage</h4>
            <p>
              Our use of TikTok data complies with the{' '}
              <a
                href="https://developers.tiktok.com/terms/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline">
                TikTok for Developers Terms of Service
              </a>
              . Specifically:
            </p>
            <ul>
              <li>
                We only use TikTok's Login Kit and Content Posting API for their intended purposes
              </li>
              <li>
                We respect creator content ownership and do not claim rights over content you
                publish
              </li>
              <li>We do not use TikTok data to build user profiles for advertising purposes</li>
              <li>
                We do not store TikTok video content on our servers; videos are transmitted directly
                to TikTok
              </li>
              <li>
                We delete TikTok authentication data promptly when you disconnect your account
              </li>
            </ul>
          </Section>

          <Section title="9. Data Usage">
            <h4 className="text-foreground font-semibold">(a) How We Use Your Data</h4>
            <p>
              We use the data collected from social media platforms and our application for the
              following purposes:
            </p>
            <div className="overflow-x-auto">
              <table className="border-border w-full border-collapse border text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="border-border border p-2 text-left">Data Type</th>
                    <th className="border-border border p-2 text-left">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-border border p-2">Profile information</td>
                    <td className="border-border border p-2">
                      Display your connected accounts, personalize your experience
                    </td>
                  </tr>
                  <tr>
                    <td className="border-border border p-2">Authentication tokens</td>
                    <td className="border-border border p-2">
                      Maintain secure connections to your social media accounts
                    </td>
                  </tr>
                  <tr>
                    <td className="border-border border p-2">Post content and metadata</td>
                    <td className="border-border border p-2">
                      Enable content creation, scheduling, and publishing
                    </td>
                  </tr>
                  <tr>
                    <td className="border-border border p-2">Usage patterns</td>
                    <td className="border-border border p-2">
                      Improve our services and user experience (with consent)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-foreground font-semibold">(b) What We Do NOT Do With Your Data</h4>
            <ul>
              <li>
                We do <strong>not</strong> sell your data to third parties
              </li>
              <li>
                We do <strong>not</strong> use your data for third-party advertising
              </li>
              <li>
                We do <strong>not</strong> use your social media content to train AI models without
                explicit consent
              </li>
              <li>
                We do <strong>not</strong> access data beyond what is necessary to provide our
                services
              </li>
            </ul>
          </Section>

          <Section title="10. Data We Collect in the App">
            <p>
              We store certain data locally on your device and in our systems to enhance your user
              experience and enable secure authentication.
            </p>

            <h4 className="text-foreground font-semibold">
              (a) Essential Storage (No Consent Required):
            </h4>
            <ul>
              <li>
                <strong>Authentication:</strong> Storing your social media login tokens
              </li>
              <li>
                <strong>User Preferences:</strong> Flags, states, timers, and other technical data
                required for smooth operation
              </li>
              <li>
                <strong>Profile Information:</strong> Cached locally for quick access after login
                (not shared externally)
              </li>
            </ul>
            <p>
              These processing activities are necessary for the app to function correctly and do not
              require consent.
            </p>

            <h4 className="text-foreground font-semibold">(b) Analytics (Requires Consent):</h4>
            <p>
              We use PostHog to collect anonymous analytics data. This helps us understand user
              behavior and improve our app and websites. PostHog may store:
            </p>
            <ul>
              <li>Session tracking cookies</li>
              <li>Interaction and event data (only after login)</li>
            </ul>
            <p>
              By default, tracking is disabled. It is enabled only if you give us your consent via
              the cookie banner or a similar consent tool.
            </p>
          </Section>

          <Section title="11. Data Sharing with Third Parties">
            <p>
              We share your data with the following categories of third parties, solely to provide
              and improve our services:
            </p>

            <h4 className="text-foreground font-semibold">(a) Social Media Platforms</h4>
            <p>
              When you publish content, we transmit that content only to the specific platform you
              select for publishing (Instagram, LinkedIn, TikTok, or YouTube) via their official
              APIs. Your content is never shared with platforms you have not explicitly chosen to
              publish to. This data transmission is necessary to fulfill the core function of our
              service.
            </p>

            <h4 className="text-foreground font-semibold">(b) Analytics Provider</h4>
            <ul>
              <li>
                <strong>PostHog</strong> (EU server: eu.i.posthog.com): Anonymous usage analytics
                (only with your consent)
              </li>
              <li>Data shared: Anonymized event data, session information</li>
              <li>
                Purpose: To understand how users interact with our application and improve our
                services
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">(c) Payment Processor</h4>
            <ul>
              <li>
                <strong>Stripe:</strong> Processes subscription payments
              </li>
              <li>Data shared: Payment method information, billing details</li>
              <li>Purpose: To process your subscription payments securely</li>
            </ul>

            <h4 className="text-foreground font-semibold">(d) Infrastructure Providers</h4>
            <ul>
              <li>
                <strong>Amazon Web Services (AWS):</strong> Cloud hosting and storage
              </li>
              <li>Data stored: User data, media files, authentication tokens (encrypted)</li>
              <li>Purpose: To host our application and store your data securely</li>
              <li>Location: EU region servers</li>
            </ul>

            <h4 className="text-foreground font-semibold">(e) No Other Third-Party Sharing</h4>
            <p>
              We do not share your data with any other third parties for marketing, advertising, or
              any other purposes not described in this policy.
            </p>
          </Section>

          <Section title="12. Data Storage and Security">
            <h4 className="text-foreground font-semibold">(a) Where Your Data Is Stored</h4>
            <ul>
              <li>
                <strong>Database:</strong> PostgreSQL database hosted on AWS (EU region)
              </li>
              <li>
                <strong>Media Files:</strong> AWS S3 (profile images, uploaded content)
              </li>
              <li>
                <strong>Authentication Tokens:</strong> Encrypted and stored in our database
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">(b) Security Measures</h4>
            <p>We implement the following security measures to protect your data:</p>
            <ul>
              <li>
                <strong>Encryption:</strong> All authentication tokens are encrypted at rest
              </li>
              <li>
                <strong>HTTPS:</strong> All data transmission uses TLS/SSL encryption
              </li>
              <li>
                <strong>Token Refresh:</strong> Automatic token refresh before expiration to
                maintain secure connections
              </li>
              <li>
                <strong>Access Control:</strong> Strict access controls limit who can access your
                data
              </li>
              <li>
                <strong>API Key Hashing:</strong> API keys are stored as secure hashes, not
                plaintext
              </li>
            </ul>

            <h4 className="text-foreground font-semibold">(c) Data Location</h4>
            <p>
              All data is processed and stored within the European Union, in compliance with GDPR
              requirements.
            </p>
            <p>
              When you use our Service to interact with third-party platforms (Instagram, LinkedIn,
              TikTok, YouTube, Google Drive), your data may be transferred to servers operated by
              these platforms, which may be located outside the European Union. These transfers are
              necessary to provide the requested functionality and are covered by appropriate
              safeguards, including Standard Contractual Clauses (SCCs) where applicable.
            </p>
          </Section>

          <Section title="13. Data Retention and Deletion">
            <h4 className="text-foreground font-semibold">(a) How Long We Keep Your Data</h4>
            <div className="overflow-x-auto">
              <table className="border-border w-full border-collapse border text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="border-border border p-2 text-left">Data Type</th>
                    <th className="border-border border p-2 text-left">Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-border border p-2">Account information</td>
                    <td className="border-border border p-2">Until you delete your account</td>
                  </tr>
                  <tr>
                    <td className="border-border border p-2">Social media tokens</td>
                    <td className="border-border border p-2">
                      Until you disconnect the platform or delete your account
                    </td>
                  </tr>
                  <tr>
                    <td className="border-border border p-2">Published post records</td>
                    <td className="border-border border p-2">Until you delete your account</td>
                  </tr>
                  <tr>
                    <td className="border-border border p-2">Log files</td>
                    <td className="border-border border p-2">Maximum 7 days</td>
                  </tr>
                  <tr>
                    <td className="border-border border p-2">Analytics data</td>
                    <td className="border-border border p-2">12 months (anonymized)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-foreground font-semibold">(b) Account Deletion</h4>
            <p>When you delete your account, the following data is permanently deleted:</p>
            <ul>
              <li>All connected social media platform credentials</li>
              <li>All posts and content created in our application</li>
              <li>All workspace and brand data</li>
              <li>All API keys</li>
              <li>Profile images stored in our systems</li>
            </ul>
            <p>This deletion is cascading and irreversible.</p>

            <h4 className="text-foreground font-semibold">(c) Platform Disconnection</h4>
            <p>When you disconnect a social media platform, the following happens immediately:</p>
            <ul>
              <li>Authentication tokens for that platform are deleted</li>
              <li>Profile images from that platform are removed from our storage</li>
              <li>Scheduled token refresh jobs are cancelled</li>
              <li>Account references are removed from your moodboards</li>
            </ul>

            <h4 className="text-foreground font-semibold">(d) How to Request Data Deletion</h4>
            <p>You can delete your data in the following ways:</p>
            <ol>
              <li>
                <strong>Delete your account:</strong> Go to Account Settings and select "Delete
                Account"
              </li>
              <li>
                <strong>Disconnect a platform:</strong> Go to Platform Settings and click
                "Disconnect" for the relevant platform
              </li>
              <li>
                <strong>Request deletion:</strong> Contact us at{' '}
                <a href="mailto:ingo@magig.de" className="text-primary hover:underline">
                  ingo@magig.de
                </a>{' '}
                to request deletion of specific data
              </li>
            </ol>
            <p>We will process deletion requests within 30 days.</p>
          </Section>

          <Section title="14. Managing Your Consent">
            <p>You can change your consent preferences at any time:</p>
            <ul>
              <li>
                <strong>Revoke Analytics Tracking:</strong>{' '}
                <a
                  href="https://getcassi.ai/manage-cookies"
                  className="text-primary hover:underline">
                  https://getcassi.ai/manage-cookies
                </a>
              </li>
              <li>
                <strong>Delete Stored Data:</strong> Clear the local storage and cookies in your
                browser for our domain(s) (e.g., magig.de, getcassi.ai) or our app
              </li>
              <li>
                <strong>Disconnect Social Media Accounts:</strong> Go to Platform Settings in the
                app to disconnect any connected account
              </li>
              <li>
                <strong>Revoke Google/YouTube Access:</strong> In addition to disconnecting within
                our app, you can revoke access to your Google account at any time via Google's
                security settings:{' '}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline">
                  https://myaccount.google.com/permissions
                </a>
              </li>
            </ul>
          </Section>

          <Section title="15. Your Rights as a Data Subject">
            <p>Under the GDPR, you have the following rights:</p>
            <ul>
              <li>
                <strong>Right of Access</strong> (Article 15 GDPR): Request a copy of your personal
                data
              </li>
              <li>
                <strong>Right to Rectification</strong> (Article 16 GDPR): Request correction of
                inaccurate data
              </li>
              <li>
                <strong>Right to Erasure</strong> (Article 17 GDPR): Request deletion of your data
                ("right to be forgotten")
              </li>
              <li>
                <strong>Right to Restrict Processing</strong> (Article 18 GDPR): Request limitation
                of data processing
              </li>
              <li>
                <strong>Right to Data Portability</strong> (Article 20 GDPR): Request your data in a
                machine-readable format
              </li>
              <li>
                <strong>Right to Object</strong> (Article 21 GDPR): Object to processing based on
                legitimate interests
              </li>
              <li>
                <strong>Right to Withdraw Consent</strong> (Article 7(3) GDPR): Withdraw consent at
                any time
              </li>
            </ul>
            <p>
              To exercise these rights, please contact:{' '}
              <a href="mailto:ingo@magig.de" className="text-primary hover:underline">
                ingo@magig.de
              </a>
            </p>
            <p>We will respond to your request within 30 days.</p>
          </Section>

          <Section title="16. Data Breach Notification">
            <p>
              In the event of a personal data breach that is likely to result in a risk to your
              rights and freedoms, we will:
            </p>
            <ul>
              <li>
                Notify the relevant supervisory authority within 72 hours of becoming aware of the
                breach, as required by GDPR Article 33
              </li>
              <li>
                Notify affected users without undue delay if the breach is likely to result in a
                high risk to your rights and freedoms, as required by GDPR Article 34
              </li>
              <li>
                Document all breaches, including the facts, effects, and remedial actions taken
              </li>
            </ul>
          </Section>

          <Section title="17. Use of AI and Compliance with the EU AI Act">
            <p>
              We may develop and use AI-based features in compliance with applicable legal
              requirements, especially the GDPR and the EU regulation on artificial intelligence (EU
              AI Act).
            </p>

            <h4 className="text-foreground font-semibold">(a) AI-Based Features:</h4>
            <p>
              If our app or services (e.g., getcassi.ai) implement AI features, we ensure that
              collected data is processed only for the intended purpose and that all data protection
              requirements are met.
            </p>

            <h4 className="text-foreground font-semibold">(b) Risk Assessment and Transparency:</h4>
            <p>
              We commit to conducting appropriate risk assessments when developing and using AI
              systems and to informing users transparently about such usage. If requirements under
              the EU AI Act change, we will adapt our processes and documentation.
            </p>

            <h4 className="text-foreground font-semibold">(c) Storage and Security:</h4>
            <p>
              All data stored as part of AI applications is protected according to applicable data
              protection standards. Data is not disclosed to third parties without explicit consent
              or another valid legal basis.
            </p>
          </Section>

          <Section title="18. Changes and Updates to This Privacy Policy">
            <p>
              We reserve the right to amend this Privacy Policy, particularly if legal requirements
              or our data processing activities change. The current version is available on our
              websites (e.g., magig.de, getcassi.ai) and/or in the app.
            </p>
            <p>If we make material changes, we will notify you via email or in-app notification.</p>
          </Section>

          <hr className="border-border my-8" />

          <footer className="text-muted-foreground text-sm">
            <p>
              <strong>MAGIG Design + Technologies</strong>
            </p>
            <p>Last updated: January 23, 2026</p>
          </footer>
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="my-8">
      <h2 className="text-foreground mb-4 text-xl font-semibold">{title}</h2>
      <div className="text-muted-foreground space-y-4">{children}</div>
    </section>
  )
}
